/**
 * The console shell and its four routes.
 *
 *   /                                  the viewer-facing landing (not here)
 *   /studio                            pick a channel
 *   /studio/{channelId}                the channel
 *   /{channelId}/{rewards|bundles|triggers}       a list
 *   /{channelId}/{kind}/{id}           one entry
 *   /{channelId}/bundles/{id}/simulate open it a thousand times, writing nothing
 *
 * The channel id is IN THE URL, always. A console that kept "which channel"
 * in memory would be one where a bookmark, a reload or a second tab lands you
 * somewhere you did not ask for — and on a multi-tenant admin tool, landing on
 * the wrong tenant is not a cosmetic bug.
 */
import type { ReactNode } from 'react';
import { SessionGate, useFeature, useSession } from './Session.jsx';
import { useRoute } from './useRoute.js';
import { communityRoute, isStudio } from './lib/router.js';
import { Landing } from './pages/Landing.jsx';
import { Legal, legalPageFor } from './pages/Legal.jsx';
import { About, isAboutPage } from './pages/About.jsx';
import { Shell } from './components/Shell.jsx';
import { ChannelPicker } from './pages/ChannelPicker.jsx';
import { ChannelHome } from './pages/ChannelHome.jsx';
import { CatalogList } from './pages/CatalogList.jsx';
import { CatalogEntry } from './pages/CatalogEntry.jsx';
import { Collection } from './pages/Collection.jsx';
import { NewReward } from './pages/NewReward.jsx';
import { NewBundle } from './pages/NewBundle.jsx';
import { NewTrigger } from './pages/NewTrigger.jsx';
import { BundleSimulator } from './pages/BundleSimulator.jsx';
import { Assets } from './pages/Assets.jsx';
import { Items } from './pages/Items.jsx';
import { Rarities } from './pages/Rarities.jsx';
import { Trades } from './pages/Trades.jsx';
import { Reports } from './pages/Reports.jsx';
import { CommunityHome } from './pages/CommunityHome.jsx';
import { CommunityAdmin } from './pages/CommunityAdmin.jsx';
import { TradeDetail } from './pages/TradeDetail.jsx';
import { Empty } from './components/primitives.jsx';
import { CATALOG, type CatalogKind } from './lib/tomo.js';

export function App(): ReactNode {
  // The root is the viewer-facing landing and is NOT behind the session gate.
  // Reading a room needs no account, so a front page that demanded one would
  // contradict the product on its first screen.
  // The legal pages are public too, and Discord's reviewers read them signed out.
  const legal = legalPageFor(window.location.pathname);
  if (legal) return <Legal page={legal} />;
  if (isAboutPage(window.location.pathname)) return <About />;

  if (!isStudio(window.location.pathname)) {
    /*
     * A community owns its name at the apex (ADR 0047).
     *
     * Outside the session gate, like the landing page and the legal pages,
     * and for the same reason: this is the address somebody opens from a
     * link in a Discord, and a front door that asks who you are before
     * telling you whether anybody is home is not a front door.
     *
     * There is no server-side table of community slugs. The console already
     * answers every apex path the rooms app does not, so the page asks the
     * API what this name is and falls back to the landing page when the
     * answer is nothing.
     */
    const community = communityRoute(window.location.pathname);
    if (community) {
      /*
       * The admin screen is NOT behind the session gate either, and that is
       * deliberate rather than an oversight: the API refuses it without a
       * session, and refusing it here as well would mean two places that
       * decide who may run a community. The screen asks and shows the answer
       * — including "only the owner can see this", which is the rule working.
       */
      return community.admin
        ? <CommunityAdmin slug={community.slug} />
        : <CommunityHome slug={community.slug} />;
    }
    return <Landing />;
  }

  return (
    <SessionGate>
      <Routes />
    </SessionGate>
  );
}

function isCatalogKind(value: string | undefined): value is CatalogKind {
  return value !== undefined && value in CATALOG;
}

function Routes(): ReactNode {
  const session = useSession();
  const collectibles = useFeature('collectibles');
  const { segments } = useRoute();
  const [channelId, kind, id, action] = segments;

  // Behind the `collectibles` flag until it is finished: everything about
  // things you can own. Refused here as well as left out of the navigation,
  // so an old bookmark lands on a plain answer rather than half a product.
  if (!collectibles && (channelId === 'collection' || channelId === 'trades')) {
    return <NotYet />;
  }

  // Your collection spans channels, so it is not under one. A streamer is a
  // collector on their own channel too, which is why it lives here rather
  // than waiting for the viewer-facing app.
  if (channelId === 'collection') return <Collection channelId={undefined} />;
  // Trades span channels too, so they live beside the collection rather than
  // under one channel.
  if (channelId === 'trades') return kind ? <TradeDetail id={kind} /> : <Trades />;
  // Reports are about tomo, not about a channel, so they sit beside trades
  // rather than under one. The API refuses anyone who is not a platform
  // admin; this is only where the screen lives.
  if (channelId === 'reports') return <Reports />;

  if (!channelId) {
    // One channel and nothing to choose between: go straight in. Making
    // someone click through a list of one is a step that exists only because
    // the code was easier to write that way.
    const only = session.channels.length === 1 ? session.channels[0] : undefined;
    return only ? <ChannelHome channelId={only.id} /> : <ChannelPicker />;
  }

  // Checked against the SESSION, not just passed to the API. The API would
  // refuse it anyway; this is what makes the refusal legible instead of a 403
  // the user has to interpret.
  const channel = session.channels.find((c) => c.id === channelId);
  if (!channel) {
    return (
      <Shell>
        <Empty
          title="You do not administer that channel."
          hint="It may belong to someone else, or your access may have been removed."
        />
      </Shell>
    );
  }

  if (!kind) return <ChannelHome channelId={channelId} />;
  if (!collectibles) return <NotYet channelId={channelId} />;
  if (kind === 'collection') return <Collection channelId={channelId} />;
  if (kind === 'rarities') return <Rarities channelId={channelId} />;
  if (kind === 'files') return <Assets channelId={channelId} />;
  if (kind === 'items') return <Items channelId={channelId} />;
  if (!isCatalogKind(kind)) {
    return (
      <Shell channelId={channelId}>
        <Empty title={`There is no "${kind}" section.`} />
      </Shell>
    );
  }

  // `new` is a reserved id. A catalog entry's id always carries its prefix
  // (`rwd_`, `bnd_`, `trg_`), so the two can never collide.
  if (id === 'new') {
    switch (kind) {
      case 'rewards': return <NewReward channelId={channelId} />;
      case 'bundles': return <NewBundle channelId={channelId} />;
      case 'triggers': return <NewTrigger channelId={channelId} />;
    }
  }

  // /{channel}/bundles/{id}/simulate — a verb on one entry, not a fifth
  // section. `simulate` cannot collide with an entry id for the same reason
  // `new` cannot: a catalog id always carries its prefix.
  if (kind === 'bundles' && id && action === 'simulate') {
    return <BundleSimulator channelId={channelId} bundleId={id} />;
  }

  return id
    ? <CatalogEntry channelId={channelId} kind={kind} id={id} />
    : <CatalogList channelId={channelId} kind={kind} />;
}

function NotYet({ channelId }: { channelId?: string | undefined }): ReactNode {
  return (
    <Shell channelId={channelId}>
      <Empty title="This is not open yet." hint="It is still being built." />
    </Shell>
  );
}
